import styled from "@emotion/styled"
import Dialog from "@mui/material/Dialog"

const SystemConfigWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  padding: 20px 22px 24px;
  border-radius: 24px;
  background: radial-gradient(
      circle at 4% -8%,
      rgba(99, 102, 241, 0.14) 0%,
      transparent 35%
    ),
    radial-gradient(
      circle at 98% 108%,
      rgba(14, 165, 233, 0.12) 0%,
      transparent 42%
    ),
    var(--cms-content-bg);

  .heading-copy {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .page-title {
    margin: 0;
    font-size: 30px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .page-subtitle {
    margin: 0;
    color: var(--cms-text-secondary);
    font-size: 13px;
    font-weight: 500;
  }

  .new-config-btn {
    text-transform: none;
    font-weight: 700;
    border-radius: 10px;
    min-height: 40px;
    padding: 8px 16px;
    box-shadow: 0 12px 24px rgba(37, 99, 235, 0.24);
  }

  .page-surface {
    border: 1px solid var(--cms-border);
    border-radius: 18px;
    background: var(--cms-surface-secondary);
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
    padding: 14px;
  }

  .page-empty {
    border: 1px dashed var(--cms-border);
    border-radius: 12px;
    background: var(--cms-surface-muted);
    padding: 26px 16px;
    text-align: center;
    color: var(--cms-text-secondary);
    font-weight: 600;
  }

  .display-config-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    .heading {
      font-size: 20px;
      font-weight: 800;
      color: var(--cms-text-primary);
      letter-spacing: -0.01em;
    }

    .MuiButton-root {
      text-transform: none;
      border-radius: 9px;
      min-height: 36px;
      font-weight: 700;
      padding: 6px 12px;
    }
  }

  .display-code-info {
    margin-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    .label-text {
      background-color: var(--cms-surface-muted);
      font-size: 11px;
      font-weight: 700;
      display: inline-block;
      min-width: 80px;
      padding: 4px 8px;
      border-radius: 999px;
      margin: 0 8px 4px 0;
      color: var(--cms-text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    .name {
      font-size: 16px;
      font-weight: 700;
      color: var(--cms-text-primary);
    }

    .key {
      font-size: 13px;
      color: var(--cms-text-secondary);
      word-break: break-word;
    }
  }

  .json-editor {
    margin-top: 12px;
    border: 1px solid var(--cms-border);
    border-radius: 12px;
    overflow: hidden;

    .monaco-editor {
      padding: 15px 0;
    }
  }

  .config-panel {
    min-height: calc(100vh - 270px);
    width: 100%;
    display: flex;
    flex-direction: row;
    border: 1px solid var(--cms-border);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 12px 24px rgba(15, 23, 42, 0.06);
    background: var(--cms-surface-secondary);

    .config-list {
      width: min(340px, 42%);
      height: 100%;
      background-color: var(--cms-surface-muted);
      border-right: 1px solid var(--cms-border);
      overflow-y: auto;

      .config-item {
        cursor: pointer;
        padding: 12px;
        width: calc(100% - 24px);
        background-color: transparent;
        border-bottom: 1px solid var(--cms-border);
        color: var(--cms-text-primary);

        &:hover {
          background-color: var(--cms-hover-bg);
        }

        &.selected {
          background: var(--cms-selected-bg);
        }

        .info {
          .name {
            font-size: 14px;
            font-weight: 700;
            color: var(--cms-text-primary);
          }

          .key {
            display: inline-block;
            margin-top: 8px;
            font-size: 11px;
            color: var(--cms-text-secondary);
            padding: 4px 8px;
            background-color: var(--cms-surface-muted);
            border-radius: 999px;
          }
        }
      }

      .config-list-empty {
        color: var(--cms-text-secondary);
        font-weight: 600;
        padding: 18px 14px;
      }
    }

    .config-value-display {
      width: calc(100% - min(340px, 42%));
      height: auto;
      padding: 16px;
      background-color: var(--cms-surface-secondary);
    }

    .display-placeholder {
      border: 1px dashed var(--cms-border);
      border-radius: 12px;
      background: var(--cms-surface-muted);
      padding: 24px 16px;
      text-align: center;
      color: var(--cms-text-secondary);
      font-weight: 600;
    }
  }

  @media (max-width: 1020px) {
    .config-panel {
      min-height: auto;
      flex-direction: column;
    }

    .config-panel .config-list {
      width: 100%;
      max-height: 280px;
      border-right: 0;
      border-bottom: 1px solid var(--cms-border);
    }

    .config-panel .config-value-display {
      width: 100%;
    }
  }
`

const StyledSystemConfigDialog = styled(Dialog)`
  .MuiPaper-root {
    width: min(900px, calc(100vw - 28px));
    max-width: none;
    border-radius: 18px;
    border: 1px solid var(--cms-border);
    background: var(--cms-dialog-bg);
    box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  }

  .MuiDialogTitle-root {
    padding: 18px 22px 8px;
  }

  .MuiDialogContent-root {
    display: flex;
    flex-direction: column;
    padding: 10px 22px 10px;
    max-height: min(76vh, 860px);
    overflow: auto;
  }

  .MuiDialogActions-root {
    padding: 8px 22px 18px;
    gap: 10px;
  }

  .dialog-title-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .dialog-title {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    color: var(--cms-text-primary);
    letter-spacing: -0.01em;
  }

  .dialog-subtitle {
    margin: 0;
    font-size: 13px;
    color: var(--cms-text-secondary);
    font-weight: 500;
  }

  .dialog-cancel-btn,
  .dialog-submit-btn {
    text-transform: none;
    border-radius: 10px;
    min-height: 40px;
    font-weight: 700;
    padding: 8px 14px;
  }

  .dialog-submit-btn {
    box-shadow: 0 12px 24px rgba(37, 99, 235, 0.24);
  }

  .MuiFormControl-root {
    margin: 0 0 12px;
    width: 100%;
  }

  .label-text {
    margin: 2px 0 8px;
    font-size: 12px;
    color: var(--cms-text-secondary);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .error-message {
    min-height: 20px;
    color: #d32f2f;
    font-size: 0.75rem;
    line-height: 1.45;
    margin-bottom: 6px;
  }

  .json-editor {
    margin-top: 8px;
    border: 1px solid var(--cms-border);
    border-radius: 12px;
    overflow: hidden;

    .monaco-editor {
      padding: 15px 0;
    }
  }
`

export { SystemConfigWrapper, StyledSystemConfigDialog }
